import {
  Activity,
  BookOpen,
  CalendarPlus,
  Check,
  Cloud,
  Copy,
  EyeOff,
  ExternalLink,
  GalleryHorizontalEnd,
  HardDrive,
  Link2,
  Plus,
  RefreshCw,
  Search,
  Settings,
  ShieldX,
  Sparkles,
  UploadCloud,
} from "lucide-react";
import type { LucideIcon } from "lucide-react";
import { FormEvent, useCallback, useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { MediaCard } from "../../components/media/MediaCard";
import { PageHeader } from "../../components/PageHeader";
import {
  createAdminVault,
  generateAdminGuestUploadLink,
  getActivity,
  getAdminClients,
  getAdminSettings,
  getAdminVaults,
  getMedia,
  updateMediaAction,
} from "../../lib/api";
import { formatBytes, formatDate, statusLabel } from "../../lib/format";
import type {
  ActivityItem,
  AdminSettingsStatus,
  AdminVaultRecord,
  ClientRecord,
  MediaItem,
  MediaKind,
  MediaStatus,
} from "../../lib/types";

type MediaAction = "approve" | "not-approve" | "hide" | "feature" | "unfeature" | "delete";
type AdminMediaView = "all" | "pending" | "approved" | "not_approved" | "hidden" | "featured";

function absoluteUrl(path?: string) {
  if (!path) return "";
  if (/^https?:\/\//i.test(path)) return path;
  return `${window.location.origin}${path.startsWith("/") ? path : `/${path}`}`;
}

function actionLabel(action: string) {
  return action
    .replace(/_/g, " ")
    .replace(/-/g, " ")
    .replace(/\b\w/g, (letter) => letter.toUpperCase());
}

export function AdminEventsPage() {
  const [clients, setClients] = useState<ClientRecord[]>([]);
  const [vaults, setVaults] = useState<AdminVaultRecord[]>([]);
  const [selectedClientId, setSelectedClientId] = useState("");
  const [eventName, setEventName] = useState("");
  const [eventType, setEventType] = useState("");
  const [eventDate, setEventDate] = useState("");
  const [eventSlug, setEventSlug] = useState("");
  const [vaultDisplayName, setVaultDisplayName] = useState("");
  const [welcomeMessage, setWelcomeMessage] = useState("");
  const [guestUploadInstructions, setGuestUploadInstructions] = useState("");
  const [accentColor, setAccentColor] = useState("#D4AF37");
  const [coverImage, setCoverImage] = useState("");
  const [optionalLogo, setOptionalLogo] = useState("");
  const [uploadButtonText, setUploadButtonText] = useState("");
  const [confirmationMessage, setConfirmationMessage] = useState("");
  const [message, setMessage] = useState("");
  const [busy, setBusy] = useState(false);
  const [clientsLoading, setClientsLoading] = useState(true);
  const [vaultsLoading, setVaultsLoading] = useState(true);

  const loadClients = useCallback(async () => {
    setClientsLoading(true);
    try {
      const clientData = await getAdminClients();
      setClients(clientData);
      setSelectedClientId((current) =>
        clientData.some((client) => client.id === current) ? current : clientData[0]?.id || "",
      );
      return clientData;
    } finally {
      setClientsLoading(false);
    }
  }, []);

  const loadVaults = useCallback(async () => {
    setVaultsLoading(true);
    try {
      const vaultData = await getAdminVaults();
      setVaults(vaultData);
      return vaultData;
    } finally {
      setVaultsLoading(false);
    }
  }, []);

  const load = useCallback(async () => {
    setMessage("");
    const [clientResult, vaultResult] = await Promise.allSettled([loadClients(), loadVaults()]);
    const errors = [clientResult, vaultResult]
      .filter((result): result is PromiseRejectedResult => result.status === "rejected")
      .map((result) => (result.reason instanceof Error ? result.reason.message : "Unable to load admin events data."));
    if (errors.length) setMessage(errors.join(" "));
  }, [loadClients, loadVaults]);

  useEffect(() => {
    void load();

    const refreshWhenVisible = () => {
      if (document.visibilityState === "visible") void load();
    };
    window.addEventListener("focus", refreshWhenVisible);
    document.addEventListener("visibilitychange", refreshWhenVisible);
    return () => {
      window.removeEventListener("focus", refreshWhenVisible);
      document.removeEventListener("visibilitychange", refreshWhenVisible);
    };
  }, [load]);

  const selectedClient = clients.find((client) => client.id === selectedClientId);
  const visibleVaults = useMemo(
    () => vaults.filter((vault) => !selectedClientId || vault.clientId === selectedClientId),
    [selectedClientId, vaults],
  );

  const submitEvent = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    setBusy(true);
    setMessage("");
    try {
      const result = await createAdminVault({
        clientId: selectedClientId,
        eventName,
        eventSlug,
        vaultDisplayName,
        eventType,
        eventDate,
        coverImage,
        optionalLogo,
        accentColor,
        welcomeMessage,
        guestUploadInstructions,
        uploadButtonText,
        confirmationMessage,
      });
      setMessage(
        `Created ${result.event.eventName}. Guest upload link: ${
          result.guestUploadLink.share_url || result.guestUploadLink.share_url_placeholder || "generated"
        }`,
      );
      setEventName("");
      setEventType("");
      setEventDate("");
      setEventSlug("");
      setVaultDisplayName("");
      setWelcomeMessage("");
      setGuestUploadInstructions("");
      setAccentColor("#D4AF37");
      setCoverImage("");
      setOptionalLogo("");
      setUploadButtonText("");
      setConfirmationMessage("");
      await load();
    } catch (error) {
      setMessage(error instanceof Error ? error.message : "Unable to create event vault.");
    } finally {
      setBusy(false);
    }
  };

  const generateLink = async (vault: AdminVaultRecord) => {
    setBusy(true);
    setMessage("");
    try {
      const result = await generateAdminGuestUploadLink({
        clientId: vault.clientId,
        eventId: vault.eventId,
        guestUploadLinkId: vault.guestUploadLinkId,
        regenerate: Boolean(vault.guestUploadLinkId),
      });
      setMessage(`Guest upload link ready: ${result.link.share_url || result.link.share_url_placeholder || result.link.id}`);
      await load();
    } catch (error) {
      setMessage(error instanceof Error ? error.message : "Unable to generate guest upload link.");
    } finally {
      setBusy(false);
    }
  };

  const copyLink = async (url: string) => {
    await navigator.clipboard?.writeText(url);
    setMessage("Guest upload link copied.");
  };

  return (
    <div className="grid gap-8">
      <PageHeader
        eyebrow="Admin Events"
        title="Create event vaults and guest upload links."
        subtitle="Set up each client event once, generate the event-specific guest upload link, and keep every vault connected to the right client."
      />

      <section className="grid gap-6 xl:grid-cols-[.95fr_1.05fr]">
        <form onSubmit={submitEvent} className="ivory-card rounded-lg p-6 sm:p-8">
          <div className="flex items-start gap-4">
            <span className="grid size-11 place-items-center rounded-lg bg-ktd-black text-ktd-softGold">
              <CalendarPlus className="size-5" />
            </span>
            <div>
              <p className="text-xs font-extrabold uppercase tracking-[0.18em] text-[#8a6c14]">Event / Vault Setup</p>
              <h2 className="mt-2 font-display text-3xl font-bold">Create a client event vault.</h2>
            </div>
          </div>

          <div className="mt-6 grid gap-4">
            <label className="grid gap-2 text-sm font-extrabold text-ktd-rich">
              Client
              <select
                value={selectedClientId}
                onChange={(event) => setSelectedClientId(event.target.value)}
                className="form-field focus-ring rounded-lg px-4 py-3 text-sm font-bold"
                disabled={clientsLoading || !clients.length}
                required
              >
                <option value="">{clientsLoading ? "Loading clients..." : "Select a client"}</option>
                {clients.map((client) => (
                  <option key={client.id} value={client.id}>
                    {client.clientName}
                  </option>
                ))}
              </select>
            </label>
            <Field label="Event / Vault name" value={eventName} onChange={setEventName} required placeholder="Client A Birthday Vault" />
            <div className="grid gap-4 md:grid-cols-3">
              <Field label="Event type" value={eventType} onChange={setEventType} placeholder="Birthday" />
              <Field label="Event date" value={eventDate} onChange={setEventDate} type="date" />
              <Field label="Event slug" value={eventSlug} onChange={setEventSlug} placeholder="client-a-birthday" />
            </div>
            <Field label="Vault display name" value={vaultDisplayName} onChange={setVaultDisplayName} placeholder="Client A Birthday Memory Vault" />
            <Field label="Accent color" value={accentColor} onChange={setAccentColor} placeholder="#D4AF37" />
            <Field label="Cover/banner image URL" value={coverImage} onChange={setCoverImage} placeholder="https://..." />
            <Field label="Optional logo/photo URL" value={optionalLogo} onChange={setOptionalLogo} placeholder="https://..." />
            <TextField label="Welcome message" value={welcomeMessage} onChange={setWelcomeMessage} />
            <TextField label="Guest upload instructions" value={guestUploadInstructions} onChange={setGuestUploadInstructions} />
            <Field label="Upload button text" value={uploadButtonText} onChange={setUploadButtonText} placeholder="Upload Memories" />
            <TextField label="Confirmation message" value={confirmationMessage} onChange={setConfirmationMessage} />
          </div>

          <button
            type="submit"
            disabled={busy || clientsLoading || !selectedClientId}
            className="focus-ring mt-6 inline-flex items-center gap-2 rounded-full bg-ktd-black px-5 py-3 text-sm font-extrabold text-ktd-softGold disabled:opacity-50"
          >
            <Plus className="size-4" />
            Create Event Vault
          </button>
          <button
            type="button"
            onClick={() => void load()}
            disabled={clientsLoading || vaultsLoading}
            className="focus-ring mt-3 inline-flex items-center gap-2 rounded-full border border-ktd-gold/35 px-5 py-3 text-sm font-extrabold text-ktd-rich disabled:opacity-50"
          >
            <RefreshCw className="size-4" />
            Refresh Clients & Vaults
          </button>
          {message && <p className="mt-5 rounded-lg bg-white p-4 text-sm font-bold text-ktd-rich">{message}</p>}
        </form>

        <section className="grid gap-4">
          <div className="ivory-card rounded-lg p-5">
            <p className="text-xs font-extrabold uppercase tracking-[0.18em] text-[#8a6c14]">Selected Client</p>
            <h2 className="mt-2 font-display text-3xl font-bold">{selectedClient?.clientName || "No client selected"}</h2>
            <p className="mt-2 text-sm font-semibold text-ktd-muted">
              {visibleVaults.length} event vault{visibleVaults.length === 1 ? "" : "s"} connected.
            </p>
          </div>

          {visibleVaults.length === 0 ? (
            <div className="ivory-card rounded-lg p-8 text-center">
              <GalleryHorizontalEnd className="mx-auto size-10 text-[#8a6c14]" />
              <h2 className="mt-5 font-display text-3xl font-bold">No event vaults yet.</h2>
              <p className="mt-3 text-sm leading-6 text-ktd-muted">Create the first event vault for this client to generate a guest upload link.</p>
            </div>
          ) : (
            visibleVaults.map((vault) => {
              const uploadUrl = absoluteUrl(vault.guestUploadUrl);
              return (
                <article key={vault.id} className="ivory-card rounded-lg p-5">
                  <div className="flex flex-col gap-4 lg:flex-row lg:items-start lg:justify-between">
                    <div>
                      <p className="text-xs font-black uppercase tracking-[0.16em] text-[#8a6c14]">{vault.clientName}</p>
                      <h2 className="mt-2 text-xl font-extrabold text-ktd-rich">{vault.vaultName}</h2>
                      <p className="mt-1 text-sm text-ktd-muted">
                        {vault.eventName} {vault.eventDate ? `- ${formatDate(vault.eventDate)}` : ""}
                      </p>
                      <p className="mt-2 text-xs font-bold uppercase tracking-[0.14em] text-[#087f7c]">
                        Status: {vault.vaultStatus} - Uploads: {vault.uploadStatus}
                      </p>
                    </div>
                    <div className="grid grid-cols-4 gap-2 text-center text-xs font-bold text-ktd-muted">
                      <span className="rounded-lg bg-white p-3">{vault.pendingCount} pending</span>
                      <span className="rounded-lg bg-white p-3">{vault.approvedCount} approved</span>
                      <span className="rounded-lg bg-white p-3">{vault.notApprovedCount} rejected</span>
                      <span className="rounded-lg bg-white p-3">{formatBytes(vault.storageBytes)}</span>
                    </div>
                  </div>

                  {uploadUrl && (
                    <div className="mt-4 rounded-lg border border-ktd-gold/20 bg-white p-4">
                      <p className="text-xs font-black uppercase tracking-[0.16em] text-[#8a6c14]">Guest Upload Link</p>
                      <p className="mt-2 break-all text-sm font-bold text-ktd-rich">{uploadUrl}</p>
                    </div>
                  )}

                  <div className="mt-4 flex flex-wrap gap-2">
                    <button
                      type="button"
                      onClick={() => void generateLink(vault)}
                      disabled={busy}
                      className="focus-ring inline-flex items-center gap-2 rounded-full bg-ktd-black px-4 py-2 text-sm font-extrabold text-ktd-softGold disabled:opacity-50"
                    >
                      <RefreshCw className="size-4" />
                      {vault.guestUploadLinkId ? "Regenerate Link" : "Generate Link"}
                    </button>
                    {uploadUrl && (
                      <>
                        <button
                          type="button"
                          onClick={() => void copyLink(uploadUrl)}
                          className="focus-ring inline-flex items-center gap-2 rounded-full border border-ktd-gold/35 px-4 py-2 text-sm font-extrabold text-ktd-rich"
                        >
                          <Copy className="size-4" />
                          Copy Link
                        </button>
                        <a
                          href={uploadUrl}
                          target="_blank"
                          rel="noreferrer"
                          className="focus-ring inline-flex items-center gap-2 rounded-full border border-ktd-gold/35 px-4 py-2 text-sm font-extrabold text-ktd-rich"
                        >
                          <ExternalLink className="size-4" />
                          Open Upload
                        </a>
                      </>
                    )}
                    <Link
                      to="/admin/memory-media-vault"
                      className="focus-ring inline-flex items-center gap-2 rounded-full border border-[#0f9f9a]/35 px-4 py-2 text-sm font-extrabold text-[#075f5c]"
                    >
                      <HardDrive className="size-4" />
                      Vault Workspace
                    </Link>
                    {vault.galleryUrl && (
                      <a
                        href={absoluteUrl(vault.galleryUrl)}
                        target="_blank"
                        rel="noreferrer"
                        className="focus-ring inline-flex items-center gap-2 rounded-full border border-[#0f9f9a]/35 px-4 py-2 text-sm font-extrabold text-[#075f5c]"
                      >
                        <GalleryHorizontalEnd className="size-4" />
                        Gallery
                      </a>
                    )}
                  </div>
                </article>
              );
            })
          )}
        </section>
      </section>
    </div>
  );
}

export function AdminActivityLogPage() {
  const [activity, setActivity] = useState<ActivityItem[]>([]);
  const [message, setMessage] = useState("");

  useEffect(() => {
    void getActivity()
      .then(setActivity)
      .catch((error) => setMessage(error instanceof Error ? error.message : "Unable to load activity."));
  }, []);

  return (
    <div className="grid gap-8">
      <PageHeader
        eyebrow="Admin Activity Log"
        title="Recent Memory Media Vault activity."
        subtitle="A simple read-only record of client setup, vault events, guest links, uploads, and approval actions."
      />
      <section className="ivory-card rounded-lg p-6 sm:p-8">
        {message && <p className="rounded-lg bg-white p-4 text-sm font-bold text-ktd-rich">{message}</p>}
        {!message && activity.length === 0 && (
          <div className="py-10 text-center">
            <Activity className="mx-auto size-10 text-[#8a6c14]" />
            <h2 className="mt-5 font-display text-3xl font-bold">No Memory Media Vault activity has been recorded yet.</h2>
          </div>
        )}
        <div className="grid gap-3">
          {activity.map((item) => (
            <div key={item.id} className="rounded-lg border border-ktd-gold/20 bg-white p-4">
              <div className="flex flex-col gap-2 sm:flex-row sm:items-start sm:justify-between">
                <div>
                  <p className="font-extrabold text-ktd-rich">{actionLabel(item.action)}</p>
                  {item.details && <p className="mt-1 text-sm leading-6 text-ktd-muted">{item.details}</p>}
                  {item.actorEmail && <p className="mt-2 text-xs font-bold uppercase tracking-[0.14em] text-[#8a6c14]">{item.actorEmail}</p>}
                </div>
                <time className="text-sm font-bold text-ktd-muted">{formatDate(item.createdAt)}</time>
              </div>
            </div>
          ))}
        </div>
      </section>
    </div>
  );
}

export function AdminMediaPage() {
  const [media, setMedia] = useState<MediaItem[]>([]);
  const [clients, setClients] = useState<ClientRecord[]>([]);
  const [vaults, setVaults] = useState<AdminVaultRecord[]>([]);
  const [view, setView] = useState<AdminMediaView>("all");
  const [selectedClientId, setSelectedClientId] = useState("all");
  const [selectedVaultId, setSelectedVaultId] = useState("all");
  const [type, setType] = useState<MediaKind | "all">("all");
  const [search, setSearch] = useState("");
  const [message, setMessage] = useState("");
  const [loading, setLoading] = useState(true);

  const clientNameById = useMemo(
    () => Object.fromEntries(clients.map((client) => [client.id, client.clientName])),
    [clients],
  );
  const vaultById = useMemo(() => Object.fromEntries(vaults.map((vault) => [vault.id, vault])), [vaults]);
  const visibleVaultOptions = useMemo(
    () => vaults.filter((vault) => selectedClientId === "all" || vault.clientId === selectedClientId),
    [selectedClientId, vaults],
  );

  const load = async () => {
    setLoading(true);
    setMessage("");
    try {
      const [clientData, vaultData] = await Promise.all([getAdminClients(), getAdminVaults()]);
      setClients(clientData);
      setVaults(vaultData);

      const status = view === "featured" || view === "all" ? undefined : (view as MediaStatus);
      const items = await getMedia({
        status,
        featured: view === "featured" ? true : undefined,
        clientId: selectedClientId === "all" ? undefined : selectedClientId,
        vaultId: selectedVaultId === "all" ? undefined : selectedVaultId,
        type,
        search,
      });
      setMedia(items.sort((a, b) => Date.parse(b.createdAt) - Date.parse(a.createdAt)));
    } catch (error) {
      setMessage(error instanceof Error ? error.message : "Unable to load admin media.");
      setMedia([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    void load();
  }, [view, selectedClientId, selectedVaultId, type]);

  const filteredMedia = useMemo(() => {
    if (!search) return media;
    const value = search.toLowerCase();
    return media.filter((item) =>
      [
        item.fileName,
        item.uploaderName,
        item.uploaderEmail,
        item.uploaderPhone,
        item.relationship,
        item.caption,
        item.eventName,
        clientNameById[item.clientId],
        vaultById[item.vaultId || ""]?.vaultName,
      ]
        .filter(Boolean)
        .some((field) => String(field).toLowerCase().includes(value)),
    );
  }, [clientNameById, media, search, vaultById]);

  const mediaMatchesCurrentView = (item: MediaItem) => {
    if (selectedClientId !== "all" && item.clientId !== selectedClientId) return false;
    if (selectedVaultId !== "all" && item.vaultId !== selectedVaultId) return false;
    if (type !== "all" && item.fileType !== type) return false;
    if (view === "featured") return item.featured;
    if (view === "all") return item.status !== "deleted";
    return item.status === view;
  };

  const runAction = async (id: string, action: MediaAction) => {
    try {
      const result = await updateMediaAction(id, action);
      const updated = result.media;
      setMedia((current) =>
        mediaMatchesCurrentView(updated)
          ? current.map((item) => (item.id === id ? updated : item))
          : current.filter((item) => item.id !== id),
      );
    } catch (error) {
      window.alert(error instanceof Error ? error.message : "Unable to update media.");
    }
  };

  return (
    <div className="grid gap-8">
      <PageHeader
        eyebrow="Admin Media"
        title="Review uploads across all client vaults."
        subtitle="Kreatethedate admins can support client approvals without entering client-scoped review routes."
      >
        <div className="vault-rail rounded-lg border border-ktd-gold/25 bg-white/[0.06] p-5">
          <p className="text-xs font-extrabold uppercase tracking-[0.2em] text-ktd-softGold">Admin Review</p>
          <p className="mt-2 text-3xl font-black text-white">{filteredMedia.length}</p>
          <p className="mt-1 text-sm text-white/62">Media in current view</p>
        </div>
      </PageHeader>

      <section className="review-toolbar ivory-card rounded-lg p-4 sm:p-5">
        <div className="grid gap-4 xl:grid-cols-[1fr_auto_auto_auto] xl:items-center">
          <label className="relative">
            <Search className="pointer-events-none absolute left-4 top-1/2 size-4 -translate-y-1/2 text-ktd-muted" />
            <input
              value={search}
              onChange={(event) => setSearch(event.target.value)}
              onKeyDown={(event) => {
                if (event.key === "Enter") void load();
              }}
              placeholder="Search by client, vault, uploader, file, or caption"
              className="form-field focus-ring w-full rounded-lg py-3 pl-11 pr-4 text-sm font-semibold"
            />
          </label>
          <select
            value={view}
            onChange={(event) => setView(event.target.value as AdminMediaView)}
            className="form-field focus-ring rounded-lg px-4 py-3 text-sm font-bold"
          >
            <option value="all">All Media</option>
            <option value="pending">Pending</option>
            <option value="approved">Approved</option>
            <option value="not_approved">Rejected / Not Approved</option>
            <option value="hidden">Hidden / Private</option>
            <option value="featured">Featured</option>
          </select>
          <select
            value={selectedClientId}
            onChange={(event) => {
              setSelectedClientId(event.target.value);
              setSelectedVaultId("all");
            }}
            className="form-field focus-ring rounded-lg px-4 py-3 text-sm font-bold"
          >
            <option value="all">All Clients</option>
            {clients.map((client) => (
              <option key={client.id} value={client.id}>
                {client.clientName}
              </option>
            ))}
          </select>
          <select
            value={selectedVaultId}
            onChange={(event) => setSelectedVaultId(event.target.value)}
            className="form-field focus-ring rounded-lg px-4 py-3 text-sm font-bold"
          >
            <option value="all">All Vaults</option>
            {visibleVaultOptions.map((vault) => (
              <option key={vault.id} value={vault.id}>
                {vault.clientName} - {vault.vaultName}
              </option>
            ))}
          </select>
        </div>
        <div className="mt-4 flex flex-wrap gap-2">
          <select
            value={type}
            onChange={(event) => setType(event.target.value as MediaKind | "all")}
            className="form-field focus-ring rounded-lg px-4 py-3 text-sm font-bold"
          >
            <option value="all">All File Types</option>
            <option value="photo">Photos Only</option>
            <option value="video">Videos Only</option>
          </select>
          <button
            type="button"
            onClick={() => void load()}
            className="focus-ring inline-flex items-center gap-2 rounded-lg bg-ktd-black px-4 py-3 text-sm font-extrabold text-ktd-softGold"
          >
            <Search className="size-4" />
            Refresh Search
          </button>
          <AdminReviewPill icon={Check} label="Approve" />
          <AdminReviewPill icon={ShieldX} label="Do Not Approve" />
          <AdminReviewPill icon={EyeOff} label="Hide" />
          <AdminReviewPill icon={Sparkles} label="Feature" />
        </div>
      </section>

      {message && <section className="ivory-card rounded-lg p-5 text-sm font-bold text-ktd-rich">{message}</section>}

      {loading ? (
        <div className="ivory-card rounded-lg p-10 text-center font-bold text-ktd-muted">Loading admin media...</div>
      ) : filteredMedia.length ? (
        <section className="grid gap-5 md:grid-cols-2 2xl:grid-cols-3">
          {filteredMedia.map((item) => {
            const vault = vaultById[item.vaultId || ""];
            const clientName = clientNameById[item.clientId] || vault?.clientName || "Unknown Client";
            const eventName = item.eventName || vault?.eventName || "Event not labeled";
            const vaultName = vault?.vaultName || "Memory Media Vault";
            return (
              <div key={item.id} className="grid gap-3">
                <div className="rounded-lg border border-ktd-gold/20 bg-ktd-black px-4 py-3 text-white shadow-luxe">
                  <p className="text-xs font-black uppercase tracking-[0.16em] text-ktd-softGold">{clientName}</p>
                  <p className="mt-1 text-sm font-extrabold">{vaultName}</p>
                  <p className="mt-1 text-xs font-semibold text-white/64">
                    {eventName} - {item.featured ? "Featured" : statusLabel[item.status]}
                  </p>
                </div>
                <MediaCard item={item} onAction={runAction} />
              </div>
            );
          })}
        </section>
      ) : (
        <section className="ivory-card rounded-lg p-10 text-center">
          <UploadCloud className="mx-auto size-12 text-[#8a6c14]" />
          <h2 className="mt-5 font-display text-3xl font-bold">No uploads match this admin view.</h2>
          <p className="mt-3 text-ktd-muted">
            Guest uploads will appear here across all client vaults after they are submitted.
          </p>
        </section>
      )}
    </div>
  );
}

function AdminReviewPill({ icon: Icon, label }: { icon: LucideIcon; label: string }) {
  return (
    <span className="inline-flex items-center gap-2 rounded-lg border border-ktd-gold/20 bg-white px-3 py-2 text-xs font-extrabold text-ktd-muted">
      <Icon className="size-4 text-[#8a6c14]" />
      {label}
    </span>
  );
}

export function AdminStorageSetupPage() {
  return (
    <div className="grid gap-8">
      <PageHeader
        eyebrow="Admin Storage Setup"
        title="Client-owned Cloudflare setup checklist."
        subtitle="Use this page while configuring a new Memory Media Vault in the client's Cloudflare account."
      />
      <section className="grid gap-4 md:grid-cols-2">
        {[
          "Create or confirm the client's Cloudflare account.",
          "Create the R2 bucket and bind it as R2_BUCKET.",
          "Create the D1 database and bind it as D1_DATABASE.",
          "Run D1 migrations from the migrations folder.",
          "Create Turnstile widget and set TURNSTILE_SITE_KEY and TURNSTILE_SECRET_KEY.",
          "Invite Kreatethedate as collaborator only for setup and support.",
          "Set CLOUDFLARE_BILLING_URL for the client's billing CTA.",
          "Test guest upload, approval, gallery, and featured Memory Media Vault routes.",
        ].map((item) => (
          <label key={item} className="ivory-card flex items-start gap-3 rounded-lg p-4 text-sm font-bold leading-6">
            <input type="checkbox" className="mt-1 size-4 accent-ktd-gold" />
            {item}
          </label>
        ))}
      </section>
      <section className="rounded-lg bg-ktd-black p-6 text-white shadow-luxe sm:p-8">
        <Cloud className="size-9 text-ktd-softGold" />
        <h2 className="mt-5 font-display text-3xl font-bold">Storage ownership reminder</h2>
        <div className="mt-4 grid gap-4 text-sm leading-7 text-white/68">
          <p>
            When a client-owned Cloudflare account is used, billing is managed through that client's Cloudflare account.
            Kreatethedate provides setup, design, connection, and support while services are active.
          </p>
          <p>
            Storage usage depends on photo and video size and quantity. The guest upload link should not be positioned as
            unlimited storage, and large video collections may require additional storage planning.
          </p>
          <p>
            Additional storage, unusually large media collections, or extended support may require additional planning or
            fees depending on the client-owned setup.
          </p>
        </div>
      </section>
    </div>
  );
}

export function AdminSettingsPage() {
  const [settings, setSettings] = useState<AdminSettingsStatus | null>(null);
  const [message, setMessage] = useState("");

  useEffect(() => {
    void getAdminSettings()
      .then(setSettings)
      .catch((error) => setMessage(error instanceof Error ? error.message : "Unable to load settings."));
  }, []);

  return (
    <div className="grid gap-8">
      <PageHeader
        eyebrow="Admin Settings"
        title="Memory Media Vault environment status."
        subtitle="Read-only environment and support values. Editable settings are currently managed through Cloudflare environment variables."
      />
      {message && <section className="ivory-card rounded-lg p-6 text-sm font-bold text-ktd-rich">{message}</section>}
      {settings && (
        <>
          <section className="ivory-card rounded-lg p-6 sm:p-8">
            <BookOpen className="size-9 text-[#8a6c14]" />
            <h2 className="mt-5 font-display text-3xl font-bold">Need a setup guide?</h2>
            <p className="mt-4 max-w-3xl leading-7 text-ktd-muted">
              The Admin User Manual walks through client setup, event vault creation, guest upload links, review actions,
              storage ownership, and recommended practice workflow.
            </p>
            <Link
              to="/admin/user-manual"
              className="focus-ring mt-6 inline-flex rounded-full bg-ktd-black px-5 py-3 text-sm font-extrabold text-ktd-softGold"
            >
              Open User Manual
            </Link>
          </section>
          <section className="ivory-card rounded-lg p-6 sm:p-8">
            <Settings className="size-9 text-[#8a6c14]" />
            <h2 className="mt-5 font-display text-3xl font-bold">{settings.notice}</h2>
            <dl className="mt-6 grid gap-3 md:grid-cols-2">
              <SettingItem label="Admin email" value={settings.adminEmail || "Not configured"} />
              <SettingItem label="Support email" value={settings.supportEmail || "Not configured"} />
              <SettingItem label="Base URL" value={settings.publicBaseUrl || "Not configured"} />
              <SettingItem label="Dev code display" value={settings.authDevShowCode ? "Enabled for testing" : "Disabled"} />
              <SettingItem label="Photo limit" value={`${settings.maxPhotoSizeMb} MB`} />
              <SettingItem label="Video limit" value={`${settings.maxVideoSizeMb} MB`} />
              <SettingItem label="Files per upload" value={String(settings.maxFilesPerUpload)} />
              <SettingItem label="D1 binding" value={settings.bindings.d1Database} />
              <SettingItem label="R2 binding" value={settings.bindings.r2Bucket} />
            </dl>
          </section>
          <section className="grid gap-4 md:grid-cols-2">
            {Object.entries(settings.environment).map(([key, value]) => (
              <div key={key} className="ivory-card rounded-lg p-4">
                <p className="text-xs font-black uppercase tracking-[0.16em] text-[#8a6c14]">{actionLabel(key)}</p>
                <p className="mt-2 text-sm font-extrabold text-ktd-rich">{value}</p>
              </div>
            ))}
          </section>
        </>
      )}
    </div>
  );
}

export function AdminClientDetailPage() {
  return (
    <AdminSimplePage
      icon={HardDrive}
      eyebrow="Client Detail"
      title="Client details, event details, setup status, and support notes."
      copy="Use the Clients and Events sections to manage Memory Media Vault access, client records, and event vault workspaces."
      cta="/admin/clients"
      ctaLabel="Open Clients"
    />
  );
}

function AdminSimplePage({
  icon: Icon,
  eyebrow,
  title,
  copy,
  cta,
  ctaLabel = "Open",
}: {
  icon: LucideIcon;
  eyebrow: string;
  title: string;
  copy: string;
  cta?: string;
  ctaLabel?: string;
}) {
  return (
    <div className="grid gap-8">
      <PageHeader eyebrow={eyebrow} title={title} subtitle={copy} />
      <section className="ivory-card rounded-lg p-8">
        <Icon className="size-10 text-[#8a6c14]" />
        <p className="mt-4 max-w-3xl leading-7 text-ktd-muted">
          This section is connected to the protected Memory Media Vault admin area. Admins can access all clients while
          client users remain scoped to their own vault records.
        </p>
        {cta && (
          <Link to={cta} className="focus-ring mt-6 inline-flex rounded-full bg-ktd-black px-5 py-3 text-sm font-extrabold text-ktd-softGold">
            {ctaLabel}
          </Link>
        )}
      </section>
    </div>
  );
}

function Field({
  label,
  value,
  onChange,
  type = "text",
  required,
  placeholder,
}: {
  label: string;
  value: string;
  onChange: (value: string) => void;
  type?: string;
  required?: boolean;
  placeholder?: string;
}) {
  return (
    <label className="grid gap-2 text-sm font-extrabold text-ktd-rich">
      {label}
      <input
        value={value}
        onChange={(event) => onChange(event.target.value)}
        type={type}
        required={required}
        placeholder={placeholder}
        className="form-field focus-ring rounded-lg px-4 py-3 text-sm font-semibold"
      />
    </label>
  );
}

function TextField({
  label,
  value,
  onChange,
}: {
  label: string;
  value: string;
  onChange: (value: string) => void;
}) {
  return (
    <label className="grid gap-2 text-sm font-extrabold text-ktd-rich">
      {label}
      <textarea
        value={value}
        onChange={(event) => onChange(event.target.value)}
        rows={3}
        className="form-field focus-ring rounded-lg px-4 py-3 text-sm font-semibold"
      />
    </label>
  );
}

function SettingItem({ label, value }: { label: string; value: string }) {
  return (
    <div className="rounded-lg border border-ktd-gold/20 bg-white p-4">
      <dt className="text-xs font-black uppercase tracking-[0.16em] text-[#8a6c14]">{label}</dt>
      <dd className="mt-2 break-words text-sm font-extrabold text-ktd-rich">{value}</dd>
    </div>
  );
}
